import { VERSION_BASE, type Brouillon, type CleSaisie, type EnvoiContact } from './types';

/**
 * Enveloppe IndexedDB écrite à la main (pas de dépendance `idb`). Trois
 * magasins : `envois` et `brouillons` sous la clé composée
 * [utilisateurId, contactId], `meta` pour la séquence d'ordre. Dans une
 * transaction, on n'attend que des requêtes de cette transaction, jamais un
 * `fetch` : IndexedDB la validerait avant la fin du travail.
 */

export type NomMagasin = 'envois' | 'brouillons';

/** Élément tel que relu, avant toute lecture défensive (`format.ts`). */
export interface EnregistrementBrut {
  cle: IDBValidKey;
  valeur: unknown;
}

/** Opérations offertes dans une transaction, identiques en IndexedDB et en mémoire. */
export interface TransactionMagasins {
  lire(magasin: NomMagasin, cle: CleSaisie): Promise<unknown>;
  ecrire(magasin: NomMagasin, valeur: EnvoiContact | Brouillon): Promise<void>;
  effacer(magasin: NomMagasin, cle: IDBValidKey | CleSaisie): Promise<void>;
  /** Tout le magasin, éléments illisibles compris. */
  tous(magasin: NomMagasin): Promise<EnregistrementBrut[]>;
  lireMeta(cle: string): Promise<unknown>;
  ecrireMeta(cle: string, valeur: unknown): Promise<void>;
}

export interface BaseSaisies {
  readonly genre: 'indexeddb' | 'memoire';
  /** Une transaction sur les trois magasins : tout le travail est validé, ou rien. */
  executer<T>(mode: 'readonly' | 'readwrite', travail: (tx: TransactionMagasins) => Promise<T>): Promise<T>;
  fermer(): void;
}

/** La connexion n'accepte plus de transaction (Safari la coupe parfois en arrière-plan). */
export class ConnexionPerdue extends Error {
  constructor(cause: unknown) {
    super('Connexion à la base locale perdue', { cause });
    this.name = 'ConnexionPerdue';
  }
}

export interface OptionsBaseIdb {
  fabrique: IDBFactory;
  nom: string;
  /** Une autre version de l'application demande la base : l'appelant la ferme après ses dernières écritures. */
  surVersionChange: () => void;
  /** Cette version attend que d'autres onglets ferment la base. */
  surBloque?: () => void;
}

const MAGASINS = ['envois', 'brouillons', 'meta'];
const CHEMIN_CLE = ['utilisateurId', 'contactId'];

export function requete<T>(demande: IDBRequest<T>): Promise<T> {
  return new Promise((resoudre, rejeter) => {
    demande.onsuccess = () => resoudre(demande.result);
    demande.onerror = () => rejeter(demande.error ?? new DOMException('Requête refusée par la base locale', 'UnknownError'));
  });
}

const cleIdb = (cle: IDBValidKey | CleSaisie): IDBValidKey => (Array.isArray(cle) ? [...cle] : (cle as IDBValidKey));

/**
 * La structure évolue par la version de base, en cas successifs : une base
 * restée en version n reçoit toutes les étapes depuis n. Ajouter une version,
 * c'est ajouter un `case` sous le dernier, sans `break` entre les deux.
 */
function monter(db: IDBDatabase, depuis: number): void {
  switch (depuis) {
    case 0: {
      const envois = db.createObjectStore('envois', { keyPath: CHEMIN_CLE });
      envois.createIndex('parUtilisateur', 'utilisateurId');
      envois.createIndex('parOrdre', 'ordre', { unique: true });
      envois.createIndex('parExpiration', 'expireLe');
      const brouillons = db.createObjectStore('brouillons', { keyPath: CHEMIN_CLE });
      brouillons.createIndex('parUtilisateur', 'utilisateurId');
      brouillons.createIndex('parExpiration', 'expireLe');
      db.createObjectStore('meta', { keyPath: 'cle' });
    }
  }
}

function transactionMagasins(tx: IDBTransaction): TransactionMagasins {
  return {
    lire: (magasin, cle) => requete(tx.objectStore(magasin).get(cleIdb(cle))),
    async ecrire(magasin, valeur) {
      await requete(tx.objectStore(magasin).put(valeur));
    },
    async effacer(magasin, cle) {
      await requete(tx.objectStore(magasin).delete(cleIdb(cle)));
    },
    async tous(magasin) {
      const store = tx.objectStore(magasin);
      const [cles, valeurs] = await Promise.all([requete(store.getAllKeys()), requete(store.getAll())]);
      return cles.map((cle, i) => ({ cle, valeur: valeurs[i] }));
    },
    async lireMeta(cle) {
      const ligne: unknown = await requete(tx.objectStore('meta').get(cle));
      return typeof ligne === 'object' && ligne !== null ? (ligne as { valeur?: unknown }).valeur : undefined;
    },
    async ecrireMeta(cle, valeur) {
      await requete(tx.objectStore('meta').put({ cle, valeur }));
    },
  };
}

function baseIdb(db: IDBDatabase): BaseSaisies {
  let fermee = false;
  return {
    genre: 'indexeddb',
    async executer<T>(mode: 'readonly' | 'readwrite', travail: (tx: TransactionMagasins) => Promise<T>): Promise<T> {
      if (fermee) throw new Error('Base locale fermée');
      let tx: IDBTransaction;
      try {
        tx = db.transaction(MAGASINS, mode);
      } catch (erreur) {
        throw new ConnexionPerdue(erreur);
      }
      const validation = new Promise<void>((resoudre, rejeter) => {
        tx.oncomplete = () => resoudre();
        // Quota dépassé à la validation, requête en échec, abandon voulu : rien n'est écrit.
        tx.onabort = () => rejeter(tx.error ?? new DOMException('Transaction abandonnée', 'AbortError'));
      });
      // Le rejet est relu plus bas ; sans ceci, un travail en échec laisserait un rejet non traité.
      validation.catch(() => undefined);
      let resultat: T;
      try {
        resultat = await travail(transactionMagasins(tx));
      } catch (erreur) {
        try {
          tx.abort();
        } catch {
          /* déjà abandonnée par la requête en échec */
        }
        throw erreur;
      }
      await validation;
      return resultat;
    },
    fermer() {
      fermee = true;
      db.close();
    },
  };
}

/** Ouvre (et crée ou fait monter) la base ; rejette si le navigateur la refuse. */
export function ouvrirBaseIdb(options: OptionsBaseIdb): Promise<BaseSaisies> {
  return new Promise((resoudre, rejeter) => {
    let demande: IDBOpenDBRequest;
    try {
      demande = options.fabrique.open(options.nom, VERSION_BASE);
    } catch (erreur) {
      // Navigation privée ancienne, stockage désactivé : `open` lève au lieu d'échouer.
      rejeter(erreur);
      return;
    }
    demande.onupgradeneeded = (evenement) => {
      try {
        monter(demande.result, evenement.oldVersion);
      } catch (erreur) {
        demande.transaction?.abort();
        rejeter(erreur);
      }
    };
    demande.onblocked = () => options.surBloque?.();
    demande.onerror = () => rejeter(demande.error ?? new DOMException('Base locale refusée', 'UnknownError'));
    demande.onsuccess = () => {
      const db = demande.result;
      db.onversionchange = () => options.surVersionChange();
      resoudre(baseIdb(db));
    };
  });
}
